import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Clock, ChevronLeft, ChevronRight, LogOut, Shuffle, Sun, Moon, ZoomIn } from 'lucide-react';
import { contentAPI, getMediaUrl } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { ImageLightbox } from '../components/ImageLightbox';
import { toast } from 'sonner';

export default function ComprehensionEcriteExercise() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const serieId = searchParams.get('serie');
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [serie, setSerie] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(60 * 60);
  const isTimedOut = timeLeft === 0;
  const [startTime] = useState(Date.now());
  const finishedRef = useRef(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, message: '', onConfirm: null });
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    const loadSerie = async () => {
      if (!serieId) {
        toast.error('Aucune série sélectionnée');
        navigate('/modules/comprehension-ecrite');
        return;
      }

      try {
        const loadedSerie = await contentAPI.getSerie('comprehension_ecrite', serieId);
        if (!loadedSerie || loadedSerie.success === false || !loadedSerie.questions || loadedSerie.questions.length === 0) {
          toast.error('Série non trouvée ou vide');
          navigate('/modules/comprehension-ecrite');
          return;
        }

        setSerie(loadedSerie);
        setQuestions(loadedSerie.questions);
      } catch (error) {
        console.error('Error loading serie:', error);
        toast.error('Erreur lors du chargement');
        navigate('/modules/comprehension-ecrite');
      }
    };
    
    loadSerie();
  }, [serieId, navigate]);

  useEffect(() => {
    if (timeLeft > 0 && questions.length > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    } else if (timeLeft === 0 && questions.length > 0 && !finishedRef.current) {
      finishedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, questions.length]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerSelect = (option) => {
    setAnswers({ ...answers, [currentQuestion]: option });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleQuestionClick = (index) => {
    setCurrentQuestion(index);
  };

  const handleAutoFill = () => {
    const randomAnswers = {};
    questions.forEach((_, index) => {
      if (!answers[index]) {
        const options = ['A', 'B', 'C', 'D'];
        randomAnswers[index] = options[Math.floor(Math.random() * options.length)];
      }
    });
    setAnswers({ ...answers, ...randomAnswers });
  };

  const calculateScore = () => {
    let totalScore = 0;
    Object.keys(answers).forEach(key => {
      const questionIndex = parseInt(key);
      const question = questions[questionIndex];
      if (question && answers[key] === question.correctAnswer) {
        totalScore += question.points || 0;
      }
    });
    return totalScore;
  };

  const doFinish = () => {
    const score = calculateScore();
    const maxPoints = questions.reduce((sum, q) => sum + (q.points || 0), 0);
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    navigate('/modules/comprehension-ecrite/results', {
      state: {
        moduleName: 'comprehension_ecrite',
        serieName: serie?.name || 'Série',
        serieId,
        questions,
        answers,
        score,
        maxPoints: maxPoints || 699,
        timeSpent
      }
    });
  };

  const handleFinish = (force = false) => {
    if (force) { doFinish(); return; }
    setConfirmModal({
      open: true,
      message: 'Voulez-vous terminer la session et afficher vos résultats ?',
      onConfirm: doFinish
    });
  };

  const handleQuit = () => {
    setConfirmModal({
      open: true,
      message: "Êtes-vous sûr de vouloir quitter l'examen ? Votre progression sera perdue.",
      onConfirm: () => navigate('/modules/comprehension-ecrite')
    });
  };

  if (!serie || !questions || questions.length === 0) {
    return (
      <div className="h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const totalTime = 60 * 60;
  const progressPercent = (timeLeft / totalTime) * 100;
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="h-screen flex flex-col bg-gray-100 dark:bg-gray-900 overflow-hidden">
      {/* Header - Timer centré */}
      <header className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h1 className="font-semibold text-gray-900 dark:text-white text-sm md:text-base">{serie?.name || 'Compréhension Écrite'}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
              Question {currentQuestion + 1} sur {questions.length}
            </p>
          </div>
          
          <div className="flex items-center justify-center flex-1">
            <div className="flex items-center space-x-2 bg-purple-100 dark:bg-purple-900/40 px-4 md:px-6 py-2 rounded-full">
              <Clock className="h-5 w-5 md:h-6 md:w-6 text-purple-600 dark:text-purple-400" />
              <span className="text-xl md:text-2xl font-bold text-purple-600 dark:text-purple-400">{formatTime(timeLeft)}</span>
            </div>
          </div>
          
          <div className="flex-1 flex justify-end">
            <span className="text-sm text-gray-500 dark:text-gray-400 hidden md:block">
              {answeredCount}/{questions.length} répondu(es)
            </span>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-2 h-1 bg-gray-200 dark:bg-gray-700 rounded-full">
          <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Hidden on mobile */}
        <aside className="hidden md:flex flex-col w-56 lg:w-64 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex-1 overflow-y-auto p-4">
            {/* Auto-fill button */}
            {!isTimedOut && (
              <button
                onClick={handleAutoFill}
                className="w-full flex items-center justify-center space-x-2 bg-orange-500 hover:bg-orange-600 text-white py-2.5 px-4 rounded-lg font-medium mb-4 transition-colors"
                data-testid="auto-fill-btn"
              >
                <Shuffle className="h-4 w-4" />
                <span className="text-sm">Remplir auto</span>
              </button>
            )}
            {isTimedOut && (
              <div className="w-full text-center bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 py-2.5 px-4 rounded-lg font-bold text-sm mb-4">
                ⏱ Temps écoulé
              </div>
            )}

            {/* Question Navigation Grid - Improved */}
            <div className="mb-4">
              <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-3 text-sm">Questions</h3>
              <div className="grid grid-cols-6 lg:grid-cols-7 gap-1.5">
                {questions.map((_, index) => {
                  const isCurrent = currentQuestion === index;
                  const isAnswered = answers[index] !== undefined;
                  
                  return (
                    <button
                      key={index}
                      onClick={() => !isTimedOut && handleQuestionClick(index)}
                      data-testid={`question-nav-${index + 1}`}
                      disabled={isTimedOut}
                      className={`h-8 w-8 rounded-lg text-xs font-bold transition-all flex items-center justify-center ${
                        isTimedOut
                          ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed opacity-50'
                          : isCurrent
                          ? 'bg-purple-600 text-white ring-2 ring-purple-300 dark:ring-purple-500 shadow-lg scale-110'
                          : isAnswered
                          ? 'bg-emerald-500 text-white shadow-sm'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      {index + 1}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="space-y-2 text-xs mb-4">
              <div className="flex items-center space-x-2">
                <div className="h-4 w-4 rounded bg-purple-600 ring-2 ring-purple-300" />
                <span className="text-gray-600 dark:text-gray-400">Actuelle</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="h-4 w-4 rounded bg-emerald-500" />
                <span className="text-gray-600 dark:text-gray-400">Répondue</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="h-4 w-4 rounded bg-gray-200 dark:bg-gray-700" />
                <span className="text-gray-600 dark:text-gray-400">Non répondue</span>
              </div>
            </div>

            {/* Stats */}
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Progression</span>
                <span className="font-bold text-gray-900 dark:text-white">{answeredCount}/{questions.length}</span>
              </div>
              <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                <div 
                  className="h-full bg-emerald-500 rounded-full transition-all"
                  style={{ width: `${(answeredCount / questions.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </aside>

        {/* Main Question Area */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile Compact Header - Replaces long horizontal bar */}
          <div className="md:hidden flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-3 py-2">
            <div className="flex items-center justify-between">
              {/* Question selector compact */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePrevious}
                  disabled={currentQuestion === 0 || isTimedOut}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="bg-purple-100 dark:bg-purple-900/40 px-4 py-2 rounded-lg">
                  <span className="font-bold text-purple-600 dark:text-purple-400">
                    {currentQuestion + 1} / {questions.length}
                  </span>
                </div>
                <button
                  onClick={handleNext}
                  disabled={currentQuestion === questions.length - 1 || isTimedOut}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              
              {/* Progress indicator */}
              <div className="flex items-center space-x-2">
                <div className={`px-2 py-1 rounded text-xs font-bold text-white ${
                  question.level === 'A1' ? 'bg-green-500' :
                  question.level === 'A2' ? 'bg-teal-500' :
                  question.level === 'B1' ? 'bg-yellow-500' : 'bg-orange-500'
                }`}>
                  {question.level}
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {answeredCount}/{questions.length}
                </span>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            {/* Question Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border-l-4 border-purple-500 p-4 mb-4 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-purple-600 dark:text-purple-400">
                  Question {currentQuestion + 1}
                </h2>
                <div className={`px-3 py-1 rounded-full text-xs font-bold text-white ${
                  question.level === 'A1' ? 'bg-green-500' :
                  question.level === 'A2' ? 'bg-teal-500' :
                  question.level === 'B1' ? 'bg-yellow-500' :
                  question.level === 'B2' ? 'bg-orange-500' :
                  question.level === 'C1' ? 'bg-red-500' : 'bg-purple-500'
                }`}>
                  {question.level} • {question.points} pts
                </div>
              </div>
            </div>

            {/* Text to read - Scrollable */}
            {question.text && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 mb-4 max-w-3xl mx-auto">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Texte à lire :</h3>
                <div className="max-h-40 overflow-y-auto bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                    {question.text}
                  </p>
                </div>
              </div>
            )}

            {/* Image - Larger on mobile */}
            {question.imageUrl && (
              <div className="flex justify-center mb-4">
                <div
                  className="group relative bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden w-full max-w-2xl cursor-zoom-in"
                  onClick={() => setLightboxImage(getMediaUrl(question.imageUrl))}
                  role="button"
                  aria-label="Agrandir l'image"
                >
                  <img
                    src={getMediaUrl(question.imageUrl)}
                    alt={`Question ${currentQuestion + 1}`}
                    className="w-full h-auto max-h-72 sm:max-h-80 md:max-h-96 object-contain"
                    data-testid="question-image"
                    onContextMenu={(e) => e.preventDefault()}
                    draggable={false}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 dark:bg-gray-900/90 rounded-full p-2 shadow-lg">
                      <ZoomIn className="h-5 w-5 text-gray-700 dark:text-gray-200" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Instruction & Options */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 md:p-6 max-w-3xl mx-auto">
              <h3 className="text-sm md:text-base font-medium text-gray-800 dark:text-gray-200 mb-4">
                {question.instruction || "Choisissez la bonne réponse"}
              </h3>

              {/* Options Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {['A', 'B', 'C', 'D'].map((option) => (
                  <button
                    key={option}
                    onClick={() => !isTimedOut && handleAnswerSelect(option)}
                    disabled={isTimedOut}
                    data-testid={`option-${option}`}
                    className={`p-3 md:p-4 rounded-xl border-2 text-left transition-all ${
                      isTimedOut
                        ? 'opacity-60 cursor-not-allowed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/20'
                        : answers[currentQuestion] === option
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 shadow-md'
                        : 'border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                        answers[currentQuestion] === option
                          ? 'bg-purple-500 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}>
                        {option}
                      </div>
                      <span className="text-sm text-gray-700 dark:text-gray-300 pt-1">
                        {question[`option${option}`] || `Option ${option}`}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Navigation - Fixed */}
          <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3">
            <div className="flex items-center justify-between max-w-3xl mx-auto">
              {/* Left - Quit & Dark mode */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleQuit}
                  data-testid="quit-btn"
                  className="flex items-center space-x-2 px-3 py-2 rounded-xl bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="text-sm font-medium hidden sm:inline">Quitter</span>
                </button>
                <button
                  onClick={toggleTheme}
                  data-testid="theme-toggle-btn"
                  className="p-2 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {theme === 'dark' ? (
                    <Sun className="h-5 w-5 text-yellow-500" />
                  ) : (
                    <Moon className="h-5 w-5 text-gray-600" />
                  )}
                </button>
              </div>

              {/* Center - Navigation */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePrevious}
                  disabled={currentQuestion === 0 || isTimedOut}
                  data-testid="prev-btn"
                  className="flex items-center space-x-1 px-3 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                  <span className="hidden sm:inline text-sm">Préc.</span>
                </button>

                <span className="text-sm font-medium text-gray-500 dark:text-gray-400 px-2">
                  {currentQuestion + 1}/{questions.length}
                </span>

                <button
                  onClick={handleNext}
                  disabled={currentQuestion === questions.length - 1 || isTimedOut}
                  data-testid="next-btn"
                  className="flex items-center space-x-1 px-3 py-2 bg-purple-500 text-white hover:bg-purple-600 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <span className="hidden sm:inline text-sm">Suiv.</span>
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>

              {/* Right - Finish */}
              <button
                onClick={() => handleFinish(isTimedOut)}
                data-testid="finish-btn"
                className={`px-4 md:px-6 py-2 rounded-xl font-semibold transition-all shadow-lg text-sm ${
                  isTimedOut
                    ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white hover:from-red-600 hover:to-orange-600 animate-pulse'
                    : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700'
                }`}
              >
                {isTimedOut ? '⏱ Voir les résultats' : 'Terminer'}
              </button>
            </div>
          </div>
        </main>
      </div>
      {confirmModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <p className="text-gray-800 dark:text-gray-200 text-base font-medium mb-6 text-center">{confirmModal.message}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmModal({ open: false, message: '', onConfirm: null })}
                className="flex-1 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => { confirmModal.onConfirm(); setConfirmModal({ open: false, message: '', onConfirm: null }); }}
                className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-medium transition-colors"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
      <ImageLightbox
        src={lightboxImage}
        alt={`Question ${currentQuestion + 1}`}
        onClose={() => setLightboxImage(null)}
      />
    </div>
  );
}
